import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { hasDuplicateLabel } from "./ReferenceDataList";
import { assertNoValidationRejection } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";
import {
  AdminTeamProfilesDocument,
  CreateTeamProfileDocument,
  DeleteTeamProfileDocument,
  UpdateTeamProfileDocument,
} from "@/graphql/generated/operations";
import type { TeamProfileCoreFragment } from "@/graphql/generated/operations";
import { KpiBadge } from "@/components/design/KpiBadge";
import { MenuItem } from "@/components/design/MenuItem";
import { AdminMasterDetail } from "./AdminMasterDetail";
import { isReadonlyTeamProfile } from "./team-profile-presets";
import { TeamProfileCard } from "./TeamProfileCard";
import { TeamProfileCreateForm } from "./TeamProfileCreateForm";
import { neutralElementWeights, profileToDraft, weightsInput } from "./team-profile-draft";
import type { TeamProfileDraft } from "./team-profile-draft";

const PAGE_SIZE = 50;

function profileLabel(profile: TeamProfileCoreFragment): string {
  return profile.localizedName?.localized || profile.name || profile.id;
}

// Admin > Stammdaten > Teamprofil ("Power Profil"): the TeamProfile list with
// its weights. Deliberately not an EDITABLE_REF_TYPES entry — that generic list
// covers flat label/id reference data and has nowhere to put weights.
export function TeamProfilePanel() {
  const intl = useIntl();
  const { data, loading, error: queryError, refetch } = useQuery(AdminTeamProfilesDocument, {
    variables: { input: { first: PAGE_SIZE } },
  });
  const [createMutation] = useMutation(CreateTeamProfileDocument);
  const [updateMutation] = useMutation(UpdateTeamProfileDocument);
  const [deleteMutation] = useMutation(DeleteTeamProfileDocument);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null = "Neues Profil" steht rechts. Die Auswahl lebt hier und nicht in der
  // Liste, weil das Detail sie ebenfalls aufloesen muss.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftGerman, setDraftGerman] = useState("");
  const [draftEnglish, setDraftEnglish] = useState("");
  // Profil, dessen Gewichte ein neues uebernimmt; null = neutrale Basis. Der
  // Picker dafuer ist eingeklappt, bis der Admin ihn oeffnet oder dupliziert.
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);

  const profiles = data?.teamProfiles.nodes ?? [];
  const totalCount = data?.teamProfiles.totalCount ?? profiles.length;
  // Abgeleitet statt validiert: ein inzwischen geloeschtes Quellprofil faellt
  // still auf die neutrale Basis zurueck, ohne einen Reset-Pfad zu brauchen.
  const source = profiles.find((p) => p.id === sourceId) ?? null;

  // Genesis accepts duplicate names, so the guard is client-side — same rule and
  // same wording as every other Stammdaten list (hasDuplicateLabel). Checked per
  // locale: two profiles may not collide in either language, or Match's picker
  // shows two entries nobody can tell apart.
  function isDuplicateName(german: string, english: string, exceptId?: string): boolean {
    const rows = (pick: (p: (typeof profiles)[number]) => string | null | undefined) =>
      profiles.map((p) => ({ id: p.id, label: pick(p) ?? "" }));
    return (
      hasDuplicateLabel(rows((p) => p.localizedName?.german), german, exceptId) ||
      hasDuplicateLabel(rows((p) => p.localizedName?.english), english, exceptId)
    );
  }

  const createDuplicate = isDuplicateName(draftGerman.trim(), draftEnglish.trim());

  /** Returns whether the write went through, so callers only clear inputs on success. */
  async function run(work: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const result = await work();
      assertNoValidationRejection(
        result,
        intl.formatMessage({
          id: "teamProfile.saveFailed",
          defaultMessage: "The profile could not be saved.",
        }),
      );
      await refetch();
      return true;
    } catch (e) {
      setError(userErrorMessage(intl, e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  // The form only asks for the name: weights come from the chosen source
  // profile or start at the neutral baseline, and are tuned in the card after.
  async function submitCreate() {
    const german = draftGerman.trim();
    const english = draftEnglish.trim();
    if (!german || !english || createDuplicate) return;
    const created = await run(() =>
      createMutation({
        variables: {
          input: {
            data: {
              localizedName: { german, english },
              weights: weightsInput(
                source ? profileToDraft(source).elementWeights : neutralElementWeights(),
              ),
            },
          },
        },
      }),
    );
    // Only on success: a rejected create used to wipe both fields, leaving the
    // admin with an error message and no idea what they had typed.
    if (created) {
      setDraftGerman("");
      setDraftEnglish("");
      setSourceId(null);
      setSourceOpen(false);
    }
  }

  // "Duplizieren" ist eine Abkuerzung ins Anlegeformular: Quelle vorbelegt,
  // Namen als Kopie vorgeschlagen. Der Zusatz folgt der Sprache des jeweiligen
  // Namensfelds, nicht der UI-Sprache, deshalb zwei Keys mit festem Wortlaut.
  function startDuplicate(profile: TeamProfileCoreFragment) {
    const draft = profileToDraft(profile);
    setDraftGerman(
      intl.formatMessage(
        { id: "teamProfile.copyName.german", defaultMessage: "{name} (Kopie)" },
        { name: draft.german || profileLabel(profile) },
      ),
    );
    setDraftEnglish(
      intl.formatMessage(
        { id: "teamProfile.copyName.english", defaultMessage: "{name} (copy)" },
        { name: draft.english || profileLabel(profile) },
      ),
    );
    setSourceId(profile.id);
    setSourceOpen(true);
    setSelectedId(null);
  }

  const selected = profiles.find((p) => p.id === selectedId) ?? null;
  // Ein geloeschtes oder weggefiltertes Profil darf nicht zu einer leeren
  // Detailspalte fuehren: dann faellt die Auswahl auf das Anlegen zurueck.
  if (selectedId !== null && !selected && !loading) setSelectedId(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-smycles-body font-medium">
          {intl.formatMessage({ id: "teamProfile.title", defaultMessage: "Power Scorecards" })}
        </h2>
        <p className="mt-1 text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "teamProfile.sub",
            defaultMessage:
              "Named weightings across the 30 powers, offered when designing a Match. Explore, Exploit and Ausgewogen are templates from Smycles: their name and weights are fixed.",
          })}
        </p>
      </div>

      {/* Query errors get their own banner: a failing list otherwise renders as
          the empty state, which reads as "there are no profiles" instead of
          "the list could not be loaded". */}
      {queryError ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">
          {userErrorMessage(intl, queryError)}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
      ) : null}

      <AdminMasterDetail
        title={intl.formatMessage({ id: "teamProfile.title", defaultMessage: "Power Scorecards" })}
        // totalCount, nicht profiles.length: die Liste zeigt eine feste Seite,
        // der Zaehler soll den Bestand nennen — sonst steht (50) ueber einer
        // Liste, die darunter selbst "die ersten 50 von 63" schreibt.
        count={totalCount}
        list={
          <>
            <Button id="teamprofile-new" variant="primary" className="mb-3 w-full" onClick={() => setSelectedId(null)}>
              {intl.formatMessage({ id: "teamProfile.create.title", defaultMessage: "New profile" })}
            </Button>

            {loading && profiles.length === 0 ? (
              <div className="text-smycles-meta text-ink-muted">
                {intl.formatMessage({ id: "teamProfile.loading", defaultMessage: "Loading…" })}
              </div>
            ) : profiles.length === 0 ? (
              <div className="text-smycles-meta text-ink-muted">
                {intl.formatMessage({ id: "teamProfile.empty", defaultMessage: "No profiles yet." })}
              </div>
            ) : (
              <div id="teamprofile-list" className="flex flex-col gap-1">
                {profiles.map((profile) => {
                  return (
                    <MenuItem
                      key={profile.id}
                      id={`teamprofile-item-${profile.id}`}
                      active={profile.id === selectedId}
                      aria-pressed={profile.id === selectedId}
                      onClick={() => setSelectedId(profile.id)}
                      trailing={
                        isReadonlyTeamProfile(profile.id) ? (
                          // KpiBadge ohne onClick ist die etablierte read-only
                          // Tint-Pill einer Listenzeile (UI_CONVENTIONS
                          // "KpiBadge"); muted, damit sie nicht mit der
                          // Auswahltoenung der Zeile konkurriert.
                          <KpiBadge id={`teamprofile-item-${profile.id}-template`} tone="muted">
                            {intl.formatMessage({
                              id: "teamProfile.presetBadge",
                              defaultMessage: "Template",
                            })}
                          </KpiBadge>
                        ) : null
                      }
                    >
                      <span id={`teamprofile-item-${profile.id}-name`}>{profileLabel(profile)}</span>
                    </MenuItem>
                  );
                })}
              </div>
            )}

            {/* The query takes a fixed page, and nothing else caps how many profiles
                an admin can create — say so rather than let the tail vanish. */}
            {totalCount > profiles.length ? (
              <div className="mt-3 text-smycles-meta text-ink-muted">
                {intl.formatMessage(
                  {
                    id: "teamProfile.truncated",
                    defaultMessage:
                      "Showing the first {shown} of {total} profiles. Delete or rename profiles you no longer need.",
                  },
                  { shown: profiles.length, total: totalCount },
                )}
              </div>
            ) : null}
          </>
        }
      >
        {selected ? (
          <TeamProfileCard
            key={selected.id}
            profile={selected}
            busy={busy}
            isDuplicateName={(german, english) => isDuplicateName(german, english, selected.id)}
            onSave={(draft: TeamProfileDraft) =>
              void run(() =>
                updateMutation({
                  variables: {
                    id: selected.id,
                    input: {
                      data: {
                        localizedName: { german: draft.german.trim(), english: draft.english.trim() },
                        weights: weightsInput(draft.elementWeights),
                      },
                    },
                  },
                }),
              )
            }
            onDelete={() =>
              void run(() => deleteMutation({ variables: { input: { id: selected.id } } }))
            }
            onDuplicate={() => startDuplicate(selected)}
          />
        ) : (
          <TeamProfileCreateForm
            german={draftGerman}
            english={draftEnglish}
            onGermanChange={setDraftGerman}
            onEnglishChange={setDraftEnglish}
            sources={profiles.map((p) => ({ value: p.id, label: profileLabel(p) }))}
            sourceId={source?.id ?? null}
            onSourceChange={setSourceId}
            sourceOpen={sourceOpen}
            onOpenSource={() => setSourceOpen(true)}
            busy={busy}
            nameTaken={createDuplicate}
            onSubmit={() => void submitCreate()}
          />
        )}
      </AdminMasterDetail>
    </div>
  );
}
